/**
 * Shared, cross-cutting UI copy — the single source for strings that recur
 * across features. Feature-local copy lives in its own module (e.g.
 * copy/pricing.js); only genuinely reused copy belongs here.
 *
 * All exports are deep-frozen. Structure is nested namespaces so a future
 * i18n layer can key off paths (e.g. `common.cta.getStartedFree`) without
 * restructuring. Do NOT implement i18n here yet.
 *
 * Brand strings (name, tagline, domains) are NOT duplicated — import them from
 * `@/lib/siteConfig`.
 *
 * @typedef {Readonly<Record<string, any>>} CopyMap
 */
import { siteConfig } from "@/lib/siteConfig";

/** Deep-freeze a plain object tree so copy can't be mutated at runtime. */
function deepFreeze(obj) {
  Object.values(obj).forEach((v) => {
    if (v && typeof v === "object" && !Object.isFrozen(v)) deepFreeze(v);
  });
  return Object.freeze(obj);
}

/**
 * Canonical call-to-action labels. Consolidates prior variants:
 *  - "Start free" / "Get started" / "Try LinkLane free" → getStartedFree
 *  - "See pricing" / "See plans" → viewPlans
 * @type {CopyMap}
 */
export const CTA = deepFreeze({
  getStartedFree: "Get started free",
  startFree: "Start free", // short form for tight hero buttons
  viewPlans: "See pricing",
  startTrial: "Start 7-day free trial",
  upgradeToPro: "Upgrade to Pro",
  upgradeToEnterprise: "Upgrade to Enterprise",
  openLane: "Open lane", // canonical in-app create verb (brand: "lane")
  backToDashboard: "Back to dashboard",
  learnMore: "Learn more",
});

/**
 * Plan display facts — single source for plan copy (names, prices, limits,
 * trial). Note: authoritative billing amounts also exist in pricing logic +
 * the Stripe/Polar config; these are the *display* strings to keep in sync.
 * @type {CopyMap}
 */
export const PLANS = deepFreeze({
  free: { name: "Free", price: "$0", limit: 50, limitText: "50 links/month" },
  pro: { name: "Pro", price: "$19", annual: "$14", trialDays: 7, trialText: "7-day free trial" },
  enterprise: { name: "Enterprise", price: "Custom", annual: null },
});

/** Brand strings — delegated to siteConfig (do not duplicate). */
export const BRAND = deepFreeze({
  name: siteConfig.siteName,
  tagline: siteConfig.tagline,
  appDomain: siteConfig.baseUrl,
  shortHost: siteConfig.shortHost,
});

/** Recurring toast messages. Keep terse, sentence-case, trailing period. */
export const TOASTS = deepFreeze({
  copied: "Copied.",
  copyFailed: "Could not copy.",
  deleted: "Deleted.",
  saved: "Saved.",
  linkCreated: "Lane opened.", // canonical success for creating a short link
});

/** Reusable empty-state phrasing. Feature-specific empties stay local. */
export const EMPTY = deepFreeze({
  noData: "— NO DATA —",
  noCountryData: "No country data yet.",
  noReferrerData: "No referrer data yet.",
});

/** Shared upgrade / paywall prompts (plan-message ready for future gating). */
export const PAYWALL = deepFreeze({
  quotaReached: `You've used all ${PLANS.free.limit} free links this month. Upgrade to Pro for 5,000 links/month and AI.`,
  proUnlock: "Upgrade to Pro to unlock more links, AI insights, analytics, and smart routing.",
  genericUnlock: "Unlock with a plan upgrade",
});

export default deepFreeze({ CTA, PLANS, BRAND, TOASTS, EMPTY, PAYWALL });
